import { z } from 'zod';
import { IdSchema, IsoDateTimeSchema } from './common.js';
/**
 * Project discussions — one flat, chat-style thread per project (projects/{id}/discussions),
 * where any member except viewers can post, reply, react and @mention. Only an @mention
 * notifies anyone (the "discussion_mention" category); a plain message never does.
 */
export const DISCUSSION_BODY_MAX_CHARS = 4000;
export const DISCUSSION_MAX_ATTACHMENTS = 10;
/** Per file. Big enough for a phone photo or a long PDF, small enough to upload over mobile data. */
export const DISCUSSION_ATTACHMENT_MAX_BYTES = 25 * 1024 * 1024;
/** The only file types a message may carry, keyed by extension — browsers report inconsistent
 * mime types for some (a .md file is often '' or text/x-markdown), so the API decides the mime
 * from the extension rather than trusting the client's. */
export const DISCUSSION_ATTACHMENT_TYPES = {
    png: { mime: 'image/png', kind: 'image' },
    jpg: { mime: 'image/jpeg', kind: 'image' },
    jpeg: { mime: 'image/jpeg', kind: 'image' },
    gif: { mime: 'image/gif', kind: 'image' },
    webp: { mime: 'image/webp', kind: 'image' },
    pdf: { mime: 'application/pdf', kind: 'document' },
    docx: {
        mime: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        kind: 'document',
    },
    md: { mime: 'text/markdown', kind: 'document' },
};
/** The attachment type for a filename, or null when that extension isn't allowed. */
export function discussionAttachmentType(filename) {
    const ext = filename.split('.').pop()?.toLowerCase() ?? '';
    return Object.hasOwn(DISCUSSION_ATTACHMENT_TYPES, ext)
        ? DISCUSSION_ATTACHMENT_TYPES[ext]
        : null;
}
/** Stored as ids rather than the emoji themselves, so they're safe Firestore map keys. */
export const DISCUSSION_REACTIONS = {
    thumbs_up: '👍',
    heart: '❤️',
    laugh: '😂',
    party: '🎉',
    eyes: '👀',
    check: '✅',
};
export const DISCUSSION_REACTION_IDS = Object.keys(DISCUSSION_REACTIONS);
export const DiscussionReactionIdSchema = z.enum(DISCUSSION_REACTION_IDS);
/** The literal token that tags every member (besides you) in a message. */
export const DISCUSSION_MENTION_ALL_TOKEN = '@everyone';
export const DiscussionAttachmentSchema = z.object({
    id: IdSchema,
    storagePath: z.string().min(1),
    name: z.string().min(1).max(255),
    mime: z.string(),
    kind: z.enum(['image', 'document']),
    sizeBytes: z.number().int().nonnegative(),
});
/** projects/{id}/discussions/{messageId}. Deleting a message removes the document outright —
 * nothing is left behind in its place, and a reply to it simply stops showing the quote. */
export const DiscussionMessageSchema = z.object({
    id: IdSchema,
    authorUid: IdSchema,
    body: z.string().max(DISCUSSION_BODY_MAX_CHARS),
    /** Members @mentioned by name, filtered server-side to members who can see discussions. */
    mentionedUids: z.array(IdSchema).default([]),
    /** Set when the body tags {@link DISCUSSION_MENTION_ALL_TOKEN}. */
    mentionsAll: z.boolean().default(false),
    /** Everyone already notified about this message, so an edit that adds a tag notifies only the
     * newly tagged — never the same person twice. */
    notifiedUids: z.array(IdSchema).default([]),
    /** The message this one replies to. Only the id is kept: the quote is rendered from the live
     * parent, so editing or deleting the parent is reflected in every reply to it. */
    replyToId: IdSchema.nullable().default(null),
    attachments: z.array(DiscussionAttachmentSchema).max(DISCUSSION_MAX_ATTACHMENTS).default([]),
    /** Reaction id -> uids who reacted with it. */
    reactions: z.record(DiscussionReactionIdSchema, z.array(IdSchema)).default({}),
    pinnedAt: IsoDateTimeSchema.nullable().default(null),
    pinnedByUid: IdSchema.nullable().default(null),
    createdAt: IsoDateTimeSchema,
    editedAt: IsoDateTimeSchema.nullable().default(null),
});
/** A file already uploaded through POST .../discussions/attachments/upload-url. The API
 * re-checks its real size and type in Storage rather than trusting these fields. */
export const DiscussionAttachmentInputSchema = z.object({
    storagePath: z.string().min(1).max(1024),
    name: z.string().min(1).max(255),
});
export const CreateDiscussionMessageInputSchema = z
    .object({
    body: z.string().trim().max(DISCUSSION_BODY_MAX_CHARS).default(''),
    mentionedUids: z.array(IdSchema).max(100).default([]),
    mentionsAll: z.boolean().default(false),
    replyToId: IdSchema.nullable().optional(),
    attachments: z
        .array(DiscussionAttachmentInputSchema)
        .max(DISCUSSION_MAX_ATTACHMENTS)
        .default([]),
})
    .refine((m) => m.body.length > 0 || m.attachments.length > 0, {
    message: 'A message needs some text or at least one attachment',
});
/** Only the text (and the tags in it) can be edited — attachments are fixed once sent. */
export const EditDiscussionMessageInputSchema = z.object({
    body: z.string().trim().max(DISCUSSION_BODY_MAX_CHARS),
    mentionedUids: z.array(IdSchema).max(100).default([]),
    mentionsAll: z.boolean().default(false),
});
export const ToggleDiscussionReactionInputSchema = z.object({
    reaction: DiscussionReactionIdSchema,
});
export const PinDiscussionMessageInputSchema = z.object({ pinned: z.boolean() });
export const DiscussionUploadUrlInputSchema = z.object({
    filename: z.string().min(1).max(255),
    sizeBytes: z.number().int().positive().max(DISCUSSION_ATTACHMENT_MAX_BYTES),
});
/** POST .../discussions/catch-up — `since` is when the caller last read the thread (the client
 * captures it before marking anything read); absent means "the recent conversation". */
export const DiscussionCatchUpInputSchema = z.object({
    since: IsoDateTimeSchema.nullable().optional(),
});
/** jobs/{jobId}.result for a `discussion_catchup` job. */
export const DiscussionCatchUpResultSchema = z.object({
    summary: z.string(),
    messageCount: z.number().int().nonnegative(),
    /** False when nothing was new since `since`, so the summary covers the latest messages instead. */
    onlyNew: z.boolean(),
});
//# sourceMappingURL=discussion.js.map